import { Room, RoomEvent, Track, DisconnectReason } from "livekit-client";
import { markRaw } from "vue";
import store from "@/store";
import { apiPost } from "@/utils/api";
import { _screenReady } from "./cameraProctoring";

export let _room = null;
export let _adminRooms = [];

export const livekitState = {
  roomName: "",
  participantName: "student" + Math.floor(Math.random() * 1000),
  role: "student",
  remoteParticipants: [],
  isExamStarted: false,
  chatMessages: [],
  screenShareTerminated: false,
  showAdminWarning: false,
  kickedByProctor: false,
};

export const livekitGetters = {
  remoteParticipants: (state) => state.remoteParticipants,
  isExamStarted: (state) => state.isExamStarted,
  chatMessages: (state) => state.chatMessages,
  screenShareTerminated: (state) => state.screenShareTerminated,
  showAdminWarning: (state) => state.showAdminWarning,
  kickedByProctor: (state) => state.kickedByProctor,
};

export const livekitMutations = {
  SET_ROOM_INFO(state, { roomName, participantName, role }) {
    if (roomName) state.roomName = roomName;
    if (participantName) state.participantName = participantName;
    if (role) state.role = role;
  },
  SET_REMOTE_PARTICIPANTS(state, participants) {
    state.remoteParticipants = participants.map((p) => markRaw(p));
  },
  SET_IS_EXAM_STARTED(state, val) {
    state.isExamStarted = val;
  },
  ADD_CHAT_MESSAGE(state, message) {
    state.chatMessages.push(message);
  },
  SET_SCREEN_SHARE_TERMINATED(state, status) {
    state.screenShareTerminated = status;
    if (status) {
      // The `hasScreen` getter only recomputes when this ref is toggled, so
      // without this, it keeps returning its last cached value after a
      // natural end (e.g. the browser's native "Stop sharing" button), and
      // the FullscreenGate "Enable Screen Share" overlay never re-appears.
      _screenReady.value = false;
    }
  },
  SET_ADMIN_WARNING(state, val) {
    state.showAdminWarning = val;
  },
  SET_KICKED_BY_PROCTOR(state, val) {
    state.kickedByProctor = val;
  },
};

// Helper — call this after any participant/track change to sync store
function syncParticipants(commit) {
  const list = [];

  _adminRooms.forEach((adminRoom) => {
    adminRoom.remoteParticipants.forEach((participant) => {
      if (participant.permissions && !participant.permissions.canPublish) {
        return;
      }

      let publications = [];
      const pubs = participant.trackPublications;

      if (pubs instanceof Map) {
        publications = Array.from(pubs.values());
      } else if (pubs && typeof pubs.values === "function") {
        publications = Array.from(pubs.values());
      } else if (pubs && typeof pubs === "object") {
        publications = Object.values(pubs);
      }

      const cleanPublications = publications.map((pub) => {
        let sourceStr = String(pub.source);
        if (pub.source === Track.Source.Camera) sourceStr = "camera";
        else if (pub.source === Track.Source.ScreenShare)
          sourceStr = "screen_share";
        else if (pub.source === Track.Source.Microphone)
          sourceStr = "microphone";

        return {
          trackSid: pub.trackSid,
          kind: pub.kind,
          source: sourceStr,
          trackName: pub.trackName || "",
          isSubscribed: pub.isSubscribed,
          videoTrack: pub.videoTrack || pub.track || null,
          audioTrack: pub.audioTrack || null,
          track: pub.track || null,
        };
      });

      list.push({
        identity: participant.identity,
        name: participant.name,
        sid: participant.sid,
        publications: cleanPublications,
        participantRef: markRaw(participant),
        videoTrackPublications: participant.videoTrackPublications,
        roomName: adminRoom.name,
      });
    });
  });

  commit("SET_REMOTE_PARTICIPANTS", list);
}

export const livekitActions = {
  async connectAsAdmin({ commit }, { roomNames }) {
    const targetNames = roomNames || [];
    const toDisconnect = _adminRooms.filter(
      (r) => !targetNames.includes(r.name)
    );
    toDisconnect.forEach((r) => r.disconnect());
    _adminRooms = _adminRooms.filter((r) => targetNames.includes(r.name));
    syncParticipants(commit);

    if (!targetNames.length) return;

    const alreadyConnected = new Set(_adminRooms.map((r) => r.name));
    const roomsToConnect = targetNames.filter(
      (name) => !alreadyConnected.has(name)
    );

    for (const roomName of roomsToConnect) {
      const adminRoom = markRaw(
        new Room({
          adaptiveStream: true,
          dynacast: true,
          autoSubscribe: true,
        })
      );

      try {
        const res = await apiPost("/proctoring/create-room/", {
          room_name: roomName,
          participant_name: store.state.user?.full_name,
          role: "admin",
        });

        const data = res.data;
        const token = data.token;

        if (!token) {
          console.error("Full Response Data:", data);
          throw new Error(
            "Token not found in API response for room " + roomName
          );
        }

        adminRoom.on(RoomEvent.ParticipantConnected, () =>
          syncParticipants(commit)
        );
        adminRoom.on(RoomEvent.ParticipantDisconnected, () =>
          syncParticipants(commit)
        );
        adminRoom.on(RoomEvent.TrackPublished, () => syncParticipants(commit));
        adminRoom.on(RoomEvent.TrackSubscribed, () => syncParticipants(commit));
        adminRoom.on(RoomEvent.TrackUnsubscribed, () =>
          syncParticipants(commit)
        );
        adminRoom.on(RoomEvent.ParticipantMetadataChanged, () =>
          syncParticipants(commit)
        );
        adminRoom.on(RoomEvent.ParticipantPermissionsChanged, () =>
          syncParticipants(commit)
        );
        adminRoom.on(RoomEvent.Reconnected, () => syncParticipants(commit));

        adminRoom.on(RoomEvent.DataReceived, (payload, participant) => {
          try {
            const strData = new TextDecoder().decode(payload);
            const data = JSON.parse(strData);
            if (data.type === "chat") {
              commit("ADD_CHAT_MESSAGE", {
                ...data,
                senderIdentity: participant?.identity || "Student",
                roomName: adminRoom.name,
              });
            }
          } catch (e) {
            console.error("[Admin] Failed to parse DataReceived payload", e);
          }
        });

        const protocol =
          window.location.protocol === "https:" ? "wss://" : "https://";
        const host =
          process.env.VUE_APP_LIVEKIT_URL || "livekit.erp.onlinejain.com";
        const liveKitUrl = `${protocol}${host}`;

        await adminRoom.connect(liveKitUrl, token);
        _adminRooms.push(adminRoom);
      } catch (err) {
        console.error("ADMIN ROOM CONNECT FAILED for " + roomName, err);
      }
    }

    syncParticipants(commit);
  },

  async sendChatMessage(
    { commit },
    { targetIdentity, message, isFromAdmin = true, type = "chat" }
  ) {
    const payloadObj = {
      type: type,
      message,
      timestamp: Date.now(),
    };
    const encoder = new TextEncoder();
    const payload = encoder.encode(JSON.stringify(payloadObj));

    if (isFromAdmin && _adminRooms.length > 0) {
      if (targetIdentity === "all") {
        for (const r of _adminRooms) {
          try {
            await r.localParticipant.publishData(payload, {
              reliable: true,
            });
          } catch (e) {
            console.error("Chat all failed for room", r.name, e);
          }
        }
        commit("ADD_CHAT_MESSAGE", {
          ...payloadObj,
          senderIdentity: "Admin (You)",
          targetIdentity: "all",
        });
      } else {
        // Find which room the participant is in
        for (const r of _adminRooms) {
          if (r.remoteParticipants.has(targetIdentity)) {
            await r.localParticipant.publishData(payload, {
              reliable: true,
              destinationIdentities: [targetIdentity],
            });
            break;
          }
        }
        commit("ADD_CHAT_MESSAGE", {
          ...payloadObj,
          senderIdentity: "Admin (You)",
          targetIdentity,
        });
      }
    }
  },

  async startRecording({ state }) {
    try {
      await apiPost("/proctoring/start-recording/", {
        exam_id: localStorage.getItem("exam_id"),
        room_name: state.roomName,
        participant_name: state.participantName,
        role: state.role,
      });
    } catch (err) {
      console.error("[Student] Failed to start recording:", err);
    }
  },

  async connectRoom({ state, commit, dispatch }) {
    if (_room) {
      await dispatch("_publishCamera");
      await dispatch("_publishScreen");
      await dispatch("startRecording");
      return;
    }

    _room = markRaw(new Room());

    let attemptID = localStorage.getItem("exam_attempt_id");
    let attemptsWait = 0;
    while (
      (!attemptID || attemptID === "null" || attemptID === "undefined") &&
      attemptsWait < 20
    ) {
      await new Promise((r) => setTimeout(r, 250));
      attemptID = localStorage.getItem("exam_attempt_id");
      attemptsWait++;
    }

    if (attemptID === "null" || attemptID === "undefined") {
      attemptID = null;
    }

    const res = await apiPost("/proctoring/create-room/", {
      attempt_id: attemptID,
      room_name: state.roomName,
      participant_name: state.participantName,
      role: state.role,
    });

    const data = res.data;
    const token = data.token || (data.data && data.data.token);

    if (!token) {
      throw new Error(data.message || "Failed to fetch token from server.");
    }

    const protocol =
      window.location.protocol === "https:" ? "wss://" : "https://";
    const host =
      process.env.VUE_APP_LIVEKIT_URL || "livekit.erp.onlinejain.com";
    const liveKitUrl = `${protocol}${host}`;

    _room.on(RoomEvent.Disconnected, async (reason) => {
      const isScribe =
        store.getters["examEligibility/isScribe"] ||
        localStorage.getItem("is_scribe") === "true";
      const isProctoringDisabled =
        store.getters["examEligibility/isProctoringDisabled"] ||
        localStorage.getItem("is_proctoring_disabled") === "true";

      if (
        reason === DisconnectReason.PARTICIPANT_REMOVED &&
        state.isExamStarted &&
        !isScribe &&
        !isProctoringDisabled
      ) {
        try {
          await dispatch("createEvent", "TERMINATED");
        } catch (e) {
          console.error("Failed to mark event as TERMINATED", e);
        }
        commit("SET_KICKED_BY_PROCTOR", true);
        window.dispatchEvent(
          new CustomEvent("termination-alert", {
            detail: "Exam Terminated by Admin",
          })
        );
        await dispatch("terminateExam");
      } else if (
        reason === DisconnectReason.DUPLICATE_IDENTITY &&
        state.isExamStarted
      ) {
        try {
          // Record audit event for session replacement
          await dispatch("logEvent", {
            event_type: "log",
            log_data: {
              reason: "Session replaced by another device/browser",
              candidate_id: state.participantName,
              room_name: state.roomName,
              previous_sid: _room?.localParticipant?.sid || "",
              new_sid: "Unknown (Auto-Replaced by LiveKit)",
              device_info: navigator.userAgent,
              timestamp: new Date().toISOString(),
            },
          });
        } catch (e) {
          console.error("Failed to log session replacement event", e);
        }
        commit("SET_KICKED_BY_PROCTOR", true);
        window.dispatchEvent(
          new CustomEvent("termination-alert", {
            detail: {
              title: "New Session Found",
              message:
                "The examination has been opened from another device or browser. Your current session has been closed.",
            },
          })
        );
        await dispatch("terminateExam");
      }
    });

    _room.on(RoomEvent.DataReceived, (payload, participant) => {
      try {
        const strData = new TextDecoder().decode(payload);
        const data = JSON.parse(strData);
        const isScribe =
          store.getters["examEligibility/isScribe"] ||
          localStorage.getItem("is_scribe") === "true";
        const isProctoringDisabled =
          store.getters["examEligibility/isProctoringDisabled"] ||
          localStorage.getItem("is_proctoring_disabled") === "true";

        if (data.type === "chat") {
          commit("ADD_CHAT_MESSAGE", {
            ...data,
            senderIdentity: participant?.identity || "Admin",
          });
        } else if (data.type === "kick" && !isScribe && !isProctoringDisabled) {
          commit("SET_KICKED_BY_PROCTOR", true);
          window.dispatchEvent(
            new CustomEvent("termination-alert", {
              detail: "Exam Terminated by Admin",
            })
          );
        } else if (data.type === "session_replaced") {
          commit("SET_KICKED_BY_PROCTOR", true);
          window.dispatchEvent(
            new CustomEvent("termination-alert", {
              detail: {
                title: "New Session Found",
                message:
                  data.message ||
                  "Examination has been opened from another device or browser. Your current session has been closed.",
              },
            })
          );
          dispatch("terminateExam");
        } else if (data.type === "warning") {
          commit("SET_ADMIN_WARNING", true);
        }
      } catch (e) {
        console.error("[Student] Failed to parse DataReceived payload", e);
      }
    });

    try {
      await _room.connect(liveKitUrl, token);
      commit("SET_IS_EXAM_STARTED", true);
    } catch (err) {
      console.error("LiveKit connect failed:", err);
    }

    await dispatch("_publishCamera");
    await dispatch("_publishScreen");
    await dispatch("startRecording");
  },

  async disconnectLivekit() {
    if (_room) {
      await _room.disconnect();
      _room = null;
    }
    if (_adminRooms.length > 0) {
      _adminRooms.forEach((r) => r.disconnect());
      _adminRooms = [];
    }
  },
};
